import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import Form from "react-bootstrap/Form";
import Dropdown from "react-bootstrap/Dropdown";
import InputGroup from "react-bootstrap/InputGroup";
import Button from "react-bootstrap/Button";
import Alert from "react-bootstrap/Alert";
import { Event, LiquidVariables } from "../../../models/Event";
import { PdfField, TemplateFile } from "../../../models/Templates";
import { QrCodeSpec } from "../../../models/TemplateMeta";
import { FieldMappings } from "./useCreateWizard";
import { LiquidValidationResult, LiquidValidator } from "../../../utils/LiquidValidator";
import logger from "../../../utils/logging";
import { LiquidLocalizedVariable, mapLiquidVariables } from "../../../utils/liquidUtils";

interface TemplateFieldListProps {
    template: TemplateFile | null;
    event: Event | null;
    liquidVariables: LiquidVariables | null;
    fieldMappings: FieldMappings;
    onFieldMappingsChange: (mappings: FieldMappings) => void;
    qrCodes: QrCodeSpec[];
    qrCodeOverrides: Record<string, string> | null;
    onQrCodeOverridesChange: (overrides: Record<string, string> | null) => void;
    onQrFocus?: (qrId: string) => void;
    onQrHover?: (qrId: string | null) => void;
    onFieldFocus?: (fieldName: string) => void;
    onFieldHover?: (fieldName: string | null) => void;
}

type VariableGroup = {
    key: "EVENT" | "GUEST" | "GUEST_FIELD";
    label: string;
    items: LiquidLocalizedVariable[];
};

type VariablePickerDropdownProps = {
    fieldName: string;
    groups: VariableGroup[];
    onSelect: (variableId: string) => void;
    buttonLabel: string;
};

const VariablePickerDropdown: React.FC<VariablePickerDropdownProps> = ({
    fieldName,
    groups,
    onSelect,
    buttonLabel,
}) => {
    const [activeGroupKey, setActiveGroupKey] = React.useState<VariableGroup["key"]>(groups[0]?.key ?? "EVENT");
    const activeGroup = groups.find((group) => group.key === activeGroupKey) ?? groups[0];

    React.useEffect(() => {
        if (!groups.some((group) => group.key === activeGroupKey)) {
            setActiveGroupKey(groups[0]?.key ?? "EVENT");
        }
    }, [activeGroupKey, groups]);

    return (
        <Dropdown align="end" id={`input-group-dropdown-${fieldName}`} className="template-field-insert">
            <Dropdown.Toggle
                variant="outline-secondary"
                size="sm"
                title={buttonLabel}
                aria-label={buttonLabel}
                className="template-field-insert-toggle"
            >
                {"{}"}
            </Dropdown.Toggle>
            <Dropdown.Menu className="template-field-insert-menu">
                <div className="template-field-insert-groups" role="tablist" aria-label={buttonLabel}>
                    {groups.map((group) => (
                        <button
                            key={group.key}
                            type="button"
                            className={`template-field-insert-group ${
                                group.key === activeGroup?.key ? "is-active" : ""
                            }`}
                            onMouseEnter={() => setActiveGroupKey(group.key)}
                            onFocus={() => setActiveGroupKey(group.key)}
                            onClick={() => setActiveGroupKey(group.key)}
                        >
                            {group.label}
                        </button>
                    ))}
                </div>
                <div className="template-field-insert-items">
                    {activeGroup?.items.map((liquidVariable) => (
                        <Dropdown.Item
                            key={liquidVariable.id}
                            className="template-field-insert-item"
                            onClick={() => onSelect(liquidVariable.id)}
                        >
                            <div className="template-field-insert-item-name">{liquidVariable.name}</div>
                            <div className="template-field-insert-item-tag">{`{{${liquidVariable.id}}}`}</div>
                        </Dropdown.Item>
                    ))}
                </div>
            </Dropdown.Menu>
        </Dropdown>
    );
};

const TemplateFieldList: React.FC<TemplateFieldListProps> = ({
    template,
    event,
    liquidVariables,
    fieldMappings,
    onFieldMappingsChange,
    qrCodes,
    qrCodeOverrides,
    onQrCodeOverridesChange,
    onQrFocus,
    onQrHover,
    onFieldFocus,
    onFieldHover,
}) => {
    const { t } = useTranslation(["wizard", "liquid"]);
    const inputRefs = useRef<Record<string, HTMLInputElement | HTMLTextAreaElement | null>>({});
    const [qrInputValues, setQrInputValues] = useState<Record<string, string>>({});
    const qrInputRefs = useRef<Record<string, HTMLInputElement | null>>({});
    const [fieldValidationResults, setFieldValidationResults] = useState<Record<string, LiquidValidationResult>>({});
    const [qrValidationResults, setQrValidationResults] = useState<Record<string, LiquidValidationResult>>({});

    // Create LiquidValidator instance with translator
    const liquidValidator = React.useMemo(() => new LiquidValidator(t), [t]);

    const mappedVariables = mapLiquidVariables(liquidVariables ?? {}, event, t);
    const groupedVariables = React.useMemo<VariableGroup[]>(
        () => {
            const groups: VariableGroup[] = [
                {
                    key: "EVENT",
                    label: t("mapFields.variableGroups.event"),
                    items: mappedVariables.filter((variable) => variable.type === "EVENT"),
                },
                {
                    key: "GUEST",
                    label: t("mapFields.variableGroups.guest"),
                    items: mappedVariables.filter((variable) => variable.type === "GUEST"),
                },
                {
                    key: "GUEST_FIELD",
                    label: t("mapFields.variableGroups.customFields"),
                    items: mappedVariables.filter((variable) => variable.type === "GUEST_FIELD"),
                },
            ];

            return groups.filter((group) => group.items.length > 0);
        },
        [mappedVariables, t],
    );

    useEffect(() => {
        if (!template?.fields || !onFieldMappingsChange) {
            return;
        }

        const nextMappings = { ...fieldMappings };
        let mappingsChanged = false;
        template.fields.forEach((field) => {
            const current = nextMappings[field.name];
            if ((current == null || current === "") && field.defaultValue) {
                nextMappings[field.name] = field.defaultValue;
                mappingsChanged = true;
            }
        });
        if (mappingsChanged) {
            onFieldMappingsChange(nextMappings);
        }
    }, [template?.fields, fieldMappings, onFieldMappingsChange]);

    // Determine if field type is supported and get normalized type
    const getFieldTypeInfo = (fieldType: string) => {
        const normalizedType = fieldType.toLowerCase();

        if (normalizedType === "singleline" || normalizedType === "text") {
            return { supported: true, type: "singleline" };
        }

        if (normalizedType === "multiline" || normalizedType === "textarea") {
            return { supported: true, type: "multiline" };
        }

        // Log unsupported field type
        logger.warn(`Unsupported PDF field type encountered: ${fieldType}`);
        return { supported: false, type: normalizedType };
    };

    // Render input component based on field type
    const renderFieldInput = (field: PdfField, validation: LiquidValidationResult | undefined, hasError: boolean) => {
        const fieldTypeInfo = getFieldTypeInfo(field.type);

        const displayName = field.alternativeName?.trim() ? field.alternativeName : field.name;

        if (!fieldTypeInfo.supported) {
            // Unsupported field type - show error alert
            return (
                <Alert variant="warning" className="mb-2">
                    <small>
                        {t("templateFields.unsupportedFieldType", {
                            fieldName: displayName,
                            fieldType: field.type,
                        })}
                    </small>
                </Alert>
            );
        }

        if (fieldTypeInfo.type === "multiline") {
            // Multiline textarea
            return (
                <InputGroup hasValidation>
                    <Form.Control
                        as="textarea"
                        rows={2}
                        size="sm"
                        className="templateField"
                        data-templatefieldname={field.name}
                        value={fieldMappings[field.name] || ""}
                        onChange={(e) => handleChange(field.name, e.target.value)}
                        onFocus={() => onFieldFocus?.(field.name)}
                        onMouseEnter={() => onFieldHover?.(field.name)}
                        onMouseLeave={() => onFieldHover?.(null)}
                        ref={(el: HTMLTextAreaElement | null) => {
                            inputRefs.current[field.name] = el;
                        }}
                        isInvalid={hasError}
                        isValid={validation && validation.isValid && (fieldMappings[field.name] || "").trim() !== ""}
                        // placeholder={t("templateFields.multilineFieldPlaceholder")}
                    />
                    {renderDropdownButton(field.name)}
                    {hasError && (
                        <Form.Control.Feedback type="invalid">
                            <strong>{validation?.error}</strong>
                            {validation?.suggestion && (
                                <div className="text-muted mt-1">{t("templateFields.suggestionHint", { suggestion: validation.suggestion })}</div>
                            )}
                        </Form.Control.Feedback>
                    )}
                </InputGroup>
            );
        }

        // Default singleline input
        return (
            <InputGroup hasValidation>
                <Form.Control
                    type="text"
                    size="sm"
                    className="templateField"
                    data-templatefieldname={field.name}
                    value={fieldMappings[field.name] || ""}
                    onChange={(e) => handleChange(field.name, e.target.value)}
                    onFocus={() => onFieldFocus?.(field.name)}
                    onMouseEnter={() => onFieldHover?.(field.name)}
                    onMouseLeave={() => onFieldHover?.(null)}
                    ref={(el: HTMLInputElement | null) => {
                        inputRefs.current[field.name] = el;
                    }}
                    isInvalid={hasError}
                    isValid={validation && validation.isValid && (fieldMappings[field.name] || "").trim() !== ""}
                />
                {renderDropdownButton(field.name)}
                {hasError && (
                    <Form.Control.Feedback type="invalid">
                        <strong>{validation?.error}</strong>
                        {validation?.suggestion && <div className="text-muted mt-1">💡 {validation.suggestion}</div>}
                    </Form.Control.Feedback>
                )}
            </InputGroup>
        );
    };

    // Render dropdown button for liquid variables
    const renderDropdownButton = (fieldName: string) => {
        return (
            <VariablePickerDropdown
                fieldName={fieldName}
                groups={groupedVariables}
                onSelect={(variableId) => appendPlaceholder(fieldName, variableId)}
                buttonLabel={t("mapFields.insertPlaceholder")}
            />
        );
    };

    const handleChange = (name: string, value: string) => {
        const validationResult = liquidValidator.validate(value);
        setFieldValidationResults((prev) => ({
            ...prev,
            [name]: validationResult,
        }));

        if (onFieldMappingsChange) {
            onFieldMappingsChange({
                ...fieldMappings,
                [name]: value,
            });
        }
    };

    // Initialise QR input values from qrCodes when the list changes (e.g. template switch).
    // Uses the stored override if one exists, otherwise falls back to the template content.
    useEffect(() => {
        setQrInputValues((prev) => {
            const next: Record<string, string> = {};
            let changed = false;
            qrCodes.forEach((qr) => {
                const override = qrCodeOverrides?.[qr.id];
                const value = override ?? qr.content;
                if (prev[qr.id] !== value) {
                    changed = true;
                }
                next[qr.id] = value;
            });
            return changed ? next : prev;
        });
    }, [qrCodes, qrCodeOverrides]);

    const handleQrChange = (id: string, value: string) => {
        setQrInputValues((prev) => ({ ...prev, [id]: value }));

        const validationResult = liquidValidator.validate(value);
        setQrValidationResults((prev) => ({ ...prev, [id]: validationResult }));

        onQrCodeOverridesChange({ ...(qrCodeOverrides ?? {}), [id]: value });
    };

    const appendQrPlaceholder = (id: string, key: string) => {
        const currentValue = qrInputValues[id] ?? "";
        const placeholder = `{{${key}}}`;
        const input = qrInputRefs.current[id];
        const selectionStart = input?.selectionStart ?? currentValue.length;
        const selectionEnd = input?.selectionEnd ?? currentValue.length;
        const appended = currentValue.slice(0, selectionStart) + placeholder + currentValue.slice(selectionEnd);

        setQrInputValues((prev) => ({ ...prev, [id]: appended }));

        const validationResult = liquidValidator.validate(appended);
        setQrValidationResults((prev) => ({ ...prev, [id]: validationResult }));

        onQrCodeOverridesChange({ ...(qrCodeOverrides ?? {}), [id]: appended });

        const nextCaretPosition = selectionStart + placeholder.length;
        requestAnimationFrame(() => {
            const ref = qrInputRefs.current[id];
            if (!ref) return;
            ref.focus();
            ref.setSelectionRange(nextCaretPosition, nextCaretPosition);
        });
    };

    const getQrCodeLabel = (qr: QrCodeSpec, index: number): string =>
        t("mapFields.qrCodeLabel", { index: index + 1, page: qr.position.page, origin: qr.position.origin });

    const appendPlaceholder = (name: string, key: string) => {
        const currentValue = fieldMappings[name] || "";
        const placeholder = `{{${key}}}`;
        const input = inputRefs.current[name];
        const selectionStart = input?.selectionStart ?? currentValue.length;
        const selectionEnd = input?.selectionEnd ?? currentValue.length;
        const appended =
            currentValue.slice(0, selectionStart) + placeholder + currentValue.slice(selectionEnd);

        const validationResult = liquidValidator.validate(appended);
        setFieldValidationResults((prev) => ({
            ...prev,
            [name]: validationResult,
        }));

        if (onFieldMappingsChange) {
            onFieldMappingsChange({
                ...fieldMappings,
                [name]: appended,
            });
        }

        const nextCaretPosition = selectionStart + placeholder.length;
        requestAnimationFrame(() => {
            const ref = inputRefs.current[name];
            if (!ref) {
                return;
            }
            ref.focus();
            ref.setSelectionRange(nextCaretPosition, nextCaretPosition);
        });
    };

    return (
        <div>
            {template?.fields?.map((field) => {
                const validation = fieldValidationResults[field.name];
                const hasError = validation && !validation.isValid;
                const fieldTypeInfo = getFieldTypeInfo(field.type);

                return (
                    <Form.Group
                        className="form-group"
                        controlId={`formGroupTemplateField-${field.name}`}
                        key={`formGroupTemplateFieldKey-${field.name}`}
                    >
                        <Form.Label className="form-label-overline">{field.alternativeName?.trim() ? field.alternativeName : field.name}</Form.Label>

                        {renderFieldInput(field, validation, hasError)}

                    </Form.Group>
                );
            })}

            {qrCodes.length > 0 && (
                <>
                    <h4 className="form-section-title mt-3">{t("mapFields.qrCodesSection")}</h4>
                    {qrCodes.map((qr, index) => {
                        const validation = qrValidationResults[qr.id];
                        const hasError = validation && !validation.isValid;
                        const label = getQrCodeLabel(qr, index);
                        return (
                            <Form.Group
                                className="form-group"
                                controlId={`formGroupQrCode-${qr.id}`}
                                key={`formGroupQrCodeKey-${qr.id}`}
                            >
                                <Form.Label className="form-label-overline">{label}</Form.Label>
                                <InputGroup hasValidation>
                                    <Form.Control
                                        type="text"
                                        size="sm"
                                        className="templateField"
                                        data-qrcodeid={qr.id}
                                        value={qrInputValues[qr.id] ?? ""}
                                        onChange={(e) => handleQrChange(qr.id, e.target.value)}
                                        onFocus={() => onQrFocus?.(qr.id)}
                                        onMouseEnter={() => onQrHover?.(qr.id)}
                                        onMouseLeave={() => onQrHover?.(null)}
                                        ref={(el: HTMLInputElement | null) => {
                                            qrInputRefs.current[qr.id] = el;
                                        }}
                                        isInvalid={hasError}
                                        isValid={validation?.isValid && (qrInputValues[qr.id] ?? "").trim() !== ""}
                                    />
                                    <VariablePickerDropdown
                                        fieldName={qr.id}
                                        groups={groupedVariables}
                                        onSelect={(variableId) => appendQrPlaceholder(qr.id, variableId)}
                                        buttonLabel={t("mapFields.insertPlaceholder")}
                                    />
                                    {hasError && (
                                        <Form.Control.Feedback type="invalid">
                                            <strong>{validation?.error}</strong>
                                            {validation?.suggestion && (
                                                <div className="text-muted mt-1">
                                                    {t("templateFields.suggestionHint", { suggestion: validation.suggestion })}
                                                </div>
                                            )}
                                        </Form.Control.Feedback>
                                    )}
                                </InputGroup>
                            </Form.Group>
                        );
                    })}
                </>
            )}
        </div>
    );
};

export default TemplateFieldList;
